import React, { createContext, useContext, useState, useCallback } from 'react';
import { getLevel, formatLevelBadge } from '../utils/levels';

export type ToastType = 'success' | 'info' | 'warning' | 'error' | 'celebration';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
  points?: number;
  levelUp?: boolean;
  newLevel?: number;
  levelTitle?: string;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type: ToastType, options?: { duration?: number; points?: number; levelUp?: boolean; newLevel?: number; levelTitle?: string }) => void;
  removeToast: (id: string) => void;
  showMissionCompleteToast: (pointsAwarded: number, rewards: string[], currentTotalPoints: number) => void;
  showLevelUpToast: (newLevel: number, pointsTotal: number) => void;
  queueToast: (message: string, type: ToastType, options?: any) => void;
  queueMultipleToasts: (toasts: Array<{ message: string; type: ToastType; options?: any }>) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [toastQueue, setToastQueue] = useState<Array<{ message: string; type: ToastType; options?: any }>>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType, options?: { duration?: number; points?: number; levelUp?: boolean; newLevel?: number; levelTitle?: string }) => {
      const id = `${Date.now()}-${Math.random()}`;
      const duration = options?.duration || (type === 'celebration' ? 5000 : 4000);

      const newToast: Toast = {
        id,
        message,
        type,
        duration,
        points: options?.points,
        levelUp: options?.levelUp,
        newLevel: options?.newLevel,
        levelTitle: options?.levelTitle,
      };

      setToasts((prev) => [...prev, newToast]);

      // Auto-remove after duration
      setTimeout(() => {
        removeToast(id);
      }, duration);
    },
    [removeToast]
  );

  // Process toast queue sequentially
  React.useEffect(() => {
    if (toastQueue.length > 0 && !isProcessingQueue) {
      setIsProcessingQueue(true);
      const nextToast = toastQueue[0];
      
      // Show the toast
      showToast(nextToast.message, nextToast.type, nextToast.options);
      
      // Remove from queue and schedule next
      const duration = nextToast.options?.duration || (nextToast.type === 'celebration' ? 5000 : 4000);
      setTimeout(() => {
        setToastQueue((prev) => prev.slice(1));
        setIsProcessingQueue(false);
      }, duration + 800); // Add 800ms delay between toasts
    }
  }, [toastQueue, isProcessingQueue, showToast]);

  // Add toasts to queue
  const queueToast = useCallback((message: string, type: ToastType, options?: any) => {
    setToastQueue((prev) => [...prev, { message, type, options }]);
  }, []);

  // Queue multiple toasts at once
  const queueMultipleToasts = useCallback((toasts: Array<{ message: string; type: ToastType; options?: any }>) => {
    setToastQueue((prev) => [...prev, ...toasts]);
  }, []);

  const showMissionCompleteToast = useCallback(
    (pointsAwarded: number, rewards: string[], currentTotalPoints: number) => {
      const message = rewards.join('\n');
      
      // Check if this caused a level up
      const oldLevel = getLevel(currentTotalPoints - pointsAwarded).level;
      const newLevel = getLevel(currentTotalPoints).level;
      const didLevelUp = newLevel > oldLevel;
      
      if (didLevelUp) {
        const levelTitle = formatLevelBadge(newLevel);
        showToast(message, 'celebration', { 
          duration: 7000, 
          points: pointsAwarded, 
          levelUp: true, 
          newLevel,
          levelTitle 
        });
      } else {
        showToast(message, 'celebration', { duration: 6000, points: pointsAwarded });
      }
    },
    [showToast]
  );

  const showLevelUpToast = useCallback(
    (newLevel: number, pointsTotal: number) => {
      const levelTitle = formatLevelBadge(newLevel);
      const message = `Congratulations! You've reached Level ${newLevel}: ${levelTitle}!`;
      showToast(message, 'celebration', { duration: 7000, levelUp: true, newLevel, levelTitle, points: pointsTotal });
    },
    [showToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast, showMissionCompleteToast, showLevelUpToast, queueToast, queueMultipleToasts }}>
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
